import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  FolderOpen,
  History,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { Stepper } from '@/components/ui/Stepper';
import type { StepItem } from '@/components/ui/Stepper';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { ImportReportView } from '@/components/inventory/ImportReportView';
import {
  defaultSelection,
  fetchInventoryImportSession,
  inventoryImportKeys,
  useBwMapUpload,
  useInventoryImportApply,
  useInventoryImportPreview,
  useInventoryImportSessions,
} from '@/hooks/useInventoryImport';
import type {
  BwMapInvalidLine,
  BwMapUploadResult,
  ImportApplyError,
  ImportApplyResult,
  ImportSessionSummary,
  ImportSourceStats,
  InventoryImportReport,
} from '@/hooks/useInventoryImport';
import { formatNumber } from '@/lib/format';

/**
 * /inventory/import — geführter Inventar-Abgleich (Spec S8-06 §5).
 *
 * Flow: Datei wählen -> Preview (persistiert Session, schreibt sonst nichts)
 * -> Report mit selektiver Übernahme -> Apply (nur accept-Liste) -> Ergebnis.
 * Sessions überleben Reloads und sind aus dem Verlauf wiederöffenbar;
 * Re-Apply ist idempotent (Server überspringt bereits Angewandtes).
 *
 * Zweiter Abschnitt (Regel-2-Lockerung 12.08.2026): Bitwarden-Map hochladen —
 * TSV host<TAB>item_id[<TAB>user], pflegt NUR die Item-IDs zentral
 * (systems.bw_item_id). bw CLI/BW_SESSION/Secrets bleiben am Sysadmin-PC.
 */

export const Route = createFileRoute('/inventory/import')({
  component: InventoryImportPage,
});

type WizardStep = 'upload' | 'report' | 'result';

const STEPS: StepItem<WizardStep>[] = [
  { id: 'upload', label: 'Datei' },
  { id: 'report', label: 'Vorschau' },
  { id: 'result', label: 'Ergebnis' },
];

const BWMAP_INVALID_COLUMNS: ReadonlyArray<Column<BwMapInvalidLine>> = [
  {
    id: 'zeile',
    header: 'Zeile',
    className: 'font-mono text-xs',
    cell: (row) => row.line,
  },
  {
    id: 'grund',
    header: 'Grund',
    filter: 'select',
    filterValue: (row) => row.reason,
    cell: (row) => row.reason,
  },
];

const APPLY_ERROR_COLUMNS: ReadonlyArray<Column<ImportApplyError>> = [
  {
    id: 'item',
    header: 'Item',
    className: 'font-mono text-xs',
    cell: (err) => err.item_id,
  },
  {
    id: 'meldung',
    header: 'Meldung',
    cell: (err) => err.message,
  },
];

/** Vereinheitlicht Preview-Response und wiedergeöffnete Session. */
interface ActiveSession {
  sessionId: string;
  filename: string;
  stats: ImportSourceStats | null;
  report: InventoryImportReport;
  appliedAt: string | null;
  appliedSummary: ImportApplyResult | null;
}

function InventoryImportPage(): JSX.Element {
  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [active, setActive] = useState<ActiveSession | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [applyResult, setApplyResult] = useState<ImportApplyResult | null>(null);
  const [reopenPendingId, setReopenPendingId] = useState<string | null>(null);
  const [reopenError, setReopenError] = useState<unknown>(null);

  const qc = useQueryClient();
  const preview = useInventoryImportPreview();
  const apply = useInventoryImportApply();
  const sessionsQuery = useInventoryImportSessions();

  const fileIsYaml = file ? /\.ya?ml$/i.test(file.name) : false;

  const openReport = (session: ActiveSession): void => {
    setActive(session);
    setSelection(defaultSelection(session.report));
    setApplyResult(null);
    apply.reset();
    setReopenError(null);
    setStep('report');
  };

  const resetWizard = (): void => {
    setStep('upload');
    setFile(null);
    setActive(null);
    setSelection(new Set());
    setApplyResult(null);
    setReopenError(null);
    preview.reset();
    apply.reset();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
    preview.reset();
  };

  const onSubmitUpload = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!file || !fileIsYaml) return;
    preview.mutate(
      { file },
      {
        onSuccess: (data) => {
          openReport({
            sessionId: data.session_id,
            filename: data.filename,
            stats: data.stats,
            report: data.report,
            appliedAt: null,
            appliedSummary: null,
          });
        },
      }
    );
  };

  const onReopen = (sessionId: string): void => {
    setReopenError(null);
    setReopenPendingId(sessionId);
    void qc
      .fetchQuery({
        queryKey: inventoryImportKeys.session(sessionId),
        queryFn: () => fetchInventoryImportSession(sessionId),
      })
      .then((detail) => {
        openReport({
          sessionId: detail.id,
          filename: detail.filename,
          stats: detail.stats ?? null,
          report: detail.report,
          appliedAt: detail.applied_at ?? null,
          appliedSummary: detail.applied_summary ?? null,
        });
      })
      .catch((err: unknown) => {
        setReopenError(err);
      })
      .finally(() => {
        setReopenPendingId(null);
      });
  };

  const onApply = (): void => {
    if (!active || selection.size === 0) return;
    apply.mutate(
      { sessionId: active.sessionId, accept: Array.from(selection) },
      {
        onSuccess: (result) => {
          setApplyResult(result);
          setStep('result');
        },
      }
    );
  };

  const toggleItem = (itemId: string, checked: boolean): void => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  };

  const setSectionSelection = (itemIds: string[], checked: boolean): void => {
    setSelection((prev) => {
      const next = new Set(prev);
      for (const id of itemIds) {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  };

  const completedSteps: WizardStep[] = [];
  if (active) completedSteps.push('upload');
  if (applyResult) completedSteps.push('report');

  const onStepChange = (next: WizardStep): void => {
    if (next === step) return;
    if (next === 'upload') {
      // Zurück zum Anfang verwirft nur den lokalen State — die Session
      // bleibt serverseitig erhalten und ist im Verlauf wiederöffenbar.
      resetWizard();
      return;
    }
    if (next === 'report' && active) {
      setStep('report');
    }
    // 'result' ist nur über "Uebernehmen" erreichbar.
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Inventar-Abgleich</h1>
        <p className="text-muted">
          Ansible-Inventar (itkfm.yml) geführt importieren: Diff-Vorschau, selektiv
          übernehmen — nie blind überschreiben, nie löschen.
        </p>
      </header>

      <Stepper
        steps={STEPS}
        active={step}
        completed={completedSteps}
        onChange={onStepChange}
        ariaLabel="Import-Schritte"
      />

      {step === 'upload' && (
        <>
          <Card
            title="Inventar-Datei hochladen"
            description="Die Vorschau schreibt nichts — sie legt nur eine wiederaufnehmbare Session an."
          >
            <form onSubmit={onSubmitUpload} className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="inventory-file"
                  className="flex flex-col gap-2 text-sm font-medium text-text"
                >
                  YAML-Inventar (.yml / .yaml)
                  <input
                    id="inventory-file"
                    type="file"
                    accept=".yml,.yaml,application/yaml,text/yaml"
                    onChange={onFileChange}
                    aria-describedby="inventory-file-hint"
                    className="block w-full rounded-md border border-border bg-surface p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm hover:file:bg-border/40"
                  />
                </label>
                <p id="inventory-file-hint" className="mt-1 text-xs text-muted">
                  Quelle: build-inventory.py (itkfm.yml). Bitwarden-Item-IDs werden
                  serverseitig verworfen — gespeichert wird nur das Flag
                  &quot;Zugangsdaten vorhanden&quot;.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  loading={preview.isPending}
                  disabled={!file || !fileIsYaml}
                  leftIcon={<Upload className="size-4" />}
                >
                  Vorschau erstellen
                </Button>
                {file && (
                  <span className="inline-flex items-center gap-2 text-sm text-muted">
                    <FileText className="size-4" aria-hidden="true" />
                    {file.name} ({formatNumber(Math.round(file.size / 1024))} KB)
                  </span>
                )}
                {file && !fileIsYaml && (
                  <Badge tone="warning">Keine YAML-Datei (.yml/.yaml erwartet)</Badge>
                )}
              </div>
            </form>
          </Card>

          {preview.isError && <ErrorAlert error={preview.error} />}
          {reopenError != null && <ErrorAlert error={reopenError} />}

          <BwMapSection />

          <SessionHistory
            query={sessionsQuery}
            onReopen={onReopen}
            reopenPendingId={reopenPendingId}
          />
        </>
      )}

      {step === 'report' && active && (
        <>
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <FileText className="size-4 text-muted" aria-hidden="true" />
                <span className="font-medium">{active.filename}</span>
                {active.stats && (
                  <span className="text-muted">
                    {formatNumber(active.stats.hosts)} Hosts ·{' '}
                    {formatNumber(active.stats.groups)} Gruppen ·{' '}
                    {formatNumber(active.stats.customers)} Kunden
                  </span>
                )}
                {active.appliedAt && <Badge tone="success">Bereits angewandt</Badge>}
              </div>
              <ApplyActions
                selectionCount={selection.size}
                pending={apply.isPending}
                onCancel={resetWizard}
                onApply={onApply}
              />
            </div>
            {active.appliedAt && (
              <p className="mt-2 text-xs text-muted">
                Diese Session wurde <RelativeTime value={active.appliedAt} /> angewandt
                {active.appliedSummary?.summary
                  ? ` (${active.appliedSummary.summary})`
                  : ''}
                . Erneutes Übernehmen ist idempotent — bereits Angewandtes wird
                übersprungen.
              </p>
            )}
          </Card>

          {apply.isError && <ErrorAlert error={apply.error} />}

          <ImportReportView
            report={active.report}
            selection={selection}
            onToggleItem={toggleItem}
            onSetSection={setSectionSelection}
          />

          <div className="flex flex-wrap justify-end gap-2">
            <ApplyActions
              selectionCount={selection.size}
              pending={apply.isPending}
              onCancel={resetWizard}
              onApply={onApply}
            />
          </div>
        </>
      )}

      {step === 'result' && applyResult && (
        <ResultCard
          result={applyResult}
          onRestart={resetWizard}
          onBackToReport={active ? (): void => setStep('report') : undefined}
        />
      )}
    </div>
  );
}

function ApplyActions({
  selectionCount,
  pending,
  onCancel,
  onApply,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onApply: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" onClick={onCancel}>
        Abbrechen
      </Button>
      <Button
        onClick={onApply}
        disabled={selectionCount === 0}
        loading={pending}
        leftIcon={<CheckCircle2 className="size-4" />}
      >
        {formatNumber(selectionCount)} Einträge übernehmen
      </Button>
    </div>
  );
}

/**
 * Zweiter Abschnitt der Import-Seite: Bitwarden-Map hochladen.
 *
 * Kein Wizard-Schritt — der Upload ist ein eigenständiger, sofort wirksamer
 * Abgleich gegen die bestehenden Systeme (kein Preview/Apply, kein Löschen:
 * unbekannte Hosts landen nur in der unmatched-Liste). Der Hook invalidiert
 * den systems-Baum, damit Detailseiten die Verknüpfung sofort zeigen.
 */
function BwMapSection(): JSX.Element {
  const upload = useBwMapUpload();
  const [file, setFile] = useState<File | null>(null);

  const fileIsTsv = file ? /\.(tsv|txt)$/i.test(file.name) : false;

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
    upload.reset();
  };

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!file || !fileIsTsv) return;
    upload.mutate({ file });
  };

  return (
    <Card
      title="Bitwarden-Map hochladen"
      description="TSV wie bw-map.tsv: host<TAB>item_id[<TAB>user]. Gespeichert wird nur die Item-ID als Referenz — bw CLI, BW_SESSION und Secrets bleiben am Sysadmin-PC."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label
            htmlFor="bwmap-file"
            className="flex flex-col gap-2 text-sm font-medium text-text"
          >
            Bitwarden-Map (.tsv / .txt)
            <input
              id="bwmap-file"
              type="file"
              accept=".tsv,.txt,text/tab-separated-values,text/plain"
              onChange={onFileChange}
              aria-describedby="bwmap-file-hint"
              className="block w-full rounded-md border border-border bg-surface p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm hover:file:bg-border/40"
            />
          </label>
          <p id="bwmap-file-hint" className="mt-1 text-xs text-muted">
            IDs lokal am Sysadmin-PC ermitteln: bw list items bzw. bw-zuweisen.
            Host = Inventar-Hostname (Ansible-Alias, Fallback Hostname/FQDN).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="submit"
            loading={upload.isPending}
            disabled={!file || !fileIsTsv}
            leftIcon={<Upload className="size-4" />}
            data-testid="bwmap-upload"
          >
            Map hochladen
          </Button>
          {file && (
            <span className="inline-flex items-center gap-2 text-sm text-muted">
              <FileText className="size-4" aria-hidden="true" />
              {file.name} ({formatNumber(Math.round(file.size / 1024))} KB)
            </span>
          )}
          {file && !fileIsTsv && (
            <Badge tone="warning">Keine TSV-Datei (.tsv/.txt erwartet)</Badge>
          )}
        </div>
      </form>

      {upload.isError && (
        <div className="mt-3">
          <ErrorAlert error={upload.error} />
        </div>
      )}

      {upload.data && <BwMapResult result={upload.data} />}
    </Card>
  );
}

function BwMapResult({ result }: { result: BwMapUploadResult }): JSX.Element {
  return (
    <div className="mt-4 flex flex-col gap-3" data-testid="bwmap-result">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ResultStat label="Aktualisiert" value={result.updated} tone="success" />
        <ResultStat label="Unverändert" value={result.unchanged} tone="neutral" />
        <ResultStat
          label="Nicht zuordenbar"
          value={result.unmatched.length}
          tone={result.unmatched.length > 0 ? 'warning' : 'neutral'}
        />
        <ResultStat
          label="Ungültig"
          value={result.invalid.length}
          tone={result.invalid.length > 0 ? 'danger' : 'neutral'}
        />
      </div>

      {result.unmatched.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-medium">Nicht zuordenbare Hosts</h3>
          <p className="mb-2 text-xs text-muted">
            Kein System mit passendem Alias/Hostname/FQDN — diese Einträge wurden
            nicht übernommen.
          </p>
          <ul className="flex flex-wrap gap-1">
            {result.unmatched.map((host) => (
              <li key={host}>
                <Badge tone="warning" className="font-mono">
                  {host}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.invalid.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-danger">Ungültige Zeilen</h3>
          <DataTable
            caption="Ungültige Zeilen der Bitwarden-Map"
            columns={BWMAP_INVALID_COLUMNS}
            rows={result.invalid}
            rowKey={(row) => String(row.line)}
            storageKey="bwmap-invalid"
          />
        </div>
      )}
    </div>
  );
}

function SessionHistory({
  query,
  onReopen,
  reopenPendingId,
}: {
  query: ReturnType<typeof useInventoryImportSessions>;
  onReopen: (sessionId: string) => void;
  reopenPendingId: string | null;
}): JSX.Element {
  const columns = useMemo<ReadonlyArray<Column<ImportSessionSummary>>>(
    () => [
      {
        id: 'datei',
        header: 'Datei',
        className: 'font-medium',
        cell: (session) => session.filename,
      },
      {
        id: 'hochgeladen',
        header: 'Hochgeladen',
        filterValue: (session) => session.uploaded_at,
        cell: (session) => <RelativeTime value={session.uploaded_at} />,
      },
      {
        id: 'quelle',
        header: 'Quelle',
        className: 'text-xs text-muted',
        filter: 'select',
        filterValue: (session) =>
          session.stats
            ? `${formatNumber(session.stats.hosts)} Hosts / ${formatNumber(
                session.stats.customers
              )} Kunden`
            : '',
        cell: (session) =>
          session.stats
            ? `${formatNumber(session.stats.hosts)} Hosts / ${formatNumber(
                session.stats.customers
              )} Kunden`
            : '–',
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (session) => (session.applied_at ? 'Angewandt' : 'Offen'),
        cell: (session) =>
          session.applied_at ? (
            <span className="inline-flex items-center gap-2">
              <Badge tone="success">Angewandt</Badge>
              <RelativeTime value={session.applied_at} className="text-xs text-muted" />
            </span>
          ) : (
            <Badge tone="neutral">Offen</Badge>
          ),
      },
      {
        id: 'aktion',
        header: 'Aktion',
        headerClassName: 'text-right',
        className: 'text-right',
        filter: false,
        cell: (session) => (
          <Button
            size="sm"
            variant="secondary"
            loading={reopenPendingId === session.id}
            onClick={() => onReopen(session.id)}
            leftIcon={<FolderOpen className="size-4" />}
          >
            Wiederöffnen
          </Button>
        ),
      },
    ],
    [onReopen, reopenPendingId]
  );

  return (
    <Card
      title="Verlauf"
      description="Frühere Import-Sessions — wiederöffnen und (erneut) anwenden ist möglich; Re-Apply überspringt bereits Angewandtes."
    >
      {query.isLoading && <SkeletonRows rows={3} cols={4} />}
      {query.isError && <ErrorAlert error={query.error} />}
      {query.data &&
        (query.data.data.length === 0 ? (
          <EmptyState
            icon={<History className="size-8" />}
            title="Noch keine Import-Sessions"
            description="Nach dem ersten Upload erscheint hier der Verlauf."
          />
        ) : (
          <DataTable
            caption="Import-Sessions"
            columns={columns}
            rows={query.data.data}
            rowKey={(session) => session.id}
            storageKey="import-sessions"
          />
        ))}
    </Card>
  );
}

function ResultCard({
  result,
  onRestart,
  onBackToReport,
}: {
  result: ImportApplyResult;
  onRestart: () => void;
  // "| undefined" explizit: Aufrufer übergibt bedingt undefined und
  // exactOptionalPropertyTypes unterscheidet das von "Property fehlt".
  onBackToReport?: (() => void) | undefined;
}): JSX.Element {
  const errors = result.errors ?? [];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {errors.length > 0 ? (
            <AlertTriangle className="size-5 text-warning" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="size-5 text-success" aria-hidden="true" />
          )}
          Import-Ergebnis
        </span>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <ResultStat label="Übernommen" value={result.applied} tone="success" />
        <ResultStat label="Übersprungen" value={result.skipped} tone="neutral" />
        <ResultStat label="Fehler" value={errors.length} tone={errors.length > 0 ? 'danger' : 'neutral'} />
      </div>

      {result.summary && <p className="mt-3 text-sm text-muted">{result.summary}</p>}

      {errors.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium text-danger">Fehler</h3>
          <DataTable
            caption="Apply-Fehler"
            columns={APPLY_ERROR_COLUMNS}
            rows={errors}
            rowKey={(err) => err.item_id}
            storageKey="import-apply-fehler"
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onRestart} leftIcon={<RotateCcw className="size-4" />}>
          Neuen Import starten
        </Button>
        {onBackToReport && (
          <Button variant="secondary" onClick={onBackToReport}>
            Zurück zur Vorschau
          </Button>
        )}
      </div>
    </Card>
  );
}

function ResultStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'success' | 'warning' | 'danger';
}): JSX.Element {
  const toneClass = {
    neutral: 'text-text',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];
  return (
    <div className="rounded-md border border-border bg-surface-2 p-3">
      <div className={`text-xl font-semibold ${toneClass}`}>{formatNumber(value)}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
}
